import { useCallback, useEffect, useRef, useState } from 'react';
import { signOut } from '../authActions';

/**
 * Configuration from environment variables
 *
 * Required env vars (mirror of AUTH0 server-side vars):
 *   - NEXT_PUBLIC_AUTH0_SESSION_ROLLING_DURATION (should match AUTH0_SESSION_ROLLING_DURATION)
 *   - NEXT_PUBLIC_AUTH0_SESSION_ABSOLUTE_DURATION (should match AUTH0_SESSION_ABSOLUTE_DURATION)
 *
 * The warning and keep-alive intervals are calculated from the session duration.
 */
const SESSION_TIMEOUT_SECONDS = Number(process.env.NEXT_PUBLIC_AUTH0_SESSION_ROLLING_DURATION) || 1800; // Default 30 min
const ABSOLUTE_TIMEOUT_SECONDS = Number(process.env.NEXT_PUBLIC_AUTH0_SESSION_ABSOLUTE_DURATION) || 10800; // Default 3 hours
const ABSOLUTE_WARNING_THRESHOLD_SECONDS = Math.max(60, Math.min(Math.floor(ABSOLUTE_TIMEOUT_SECONDS / 10), 900)); // 10% of absolute, min 1 min, max 15 min
const KEEP_ALIVE_INTERVAL_SECONDS = Math.floor(SESSION_TIMEOUT_SECONDS / 4); // Quarter of timeout

// LocalStorage keys for cross-tab sync
const LS_SESSION_LOGIN_TIME = 'session_login_time';
const LS_WARNING_START_TIME = 'session_warning_start_time';

interface UseSessionKeepAliveReturn {
  showWarning: boolean;
  secondsRemaining: number;
  isAbsoluteExpiry: boolean; // True when warning is for absolute session (requires re-login)
  extendSession: () => Promise<void>;
  logout: () => Promise<void>;
  reLogin: () => void; // Redirect to login for absolute session expiry
  cancelWarning: () => void; // Close the warning popup without logout (to save work)
}

/**
 * Hook to manage session keep-alive and expiry warning
 * Features:
 * - Silent keep-alive (auto-extends rolling session, no idle warning popup)
 * - Warning modal only for absolute session expiry (user must re-login)
 * - Cross-tab synchronization via localStorage
 */
export const useSessionKeepAlive = (): UseSessionKeepAliveReturn => {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [isAbsoluteExpiry, setIsAbsoluteExpiry] = useState(false);

  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const keepAliveRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isWarningShownRef = useRef(false);
  const isAbsoluteWarningRef = useRef(false);
  const isWarningDismissedRef = useRef(false); // User clicked Cancel - don't show again
  const isMountedRef = useRef(true);
  const touchInFlightRef = useRef<Promise<boolean> | null>(null);

  // Get login time from localStorage (shared across tabs)
  const getLoginTime = useCallback((): number => {
    try {
      const stored = localStorage.getItem(LS_SESSION_LOGIN_TIME);
      return stored ? parseInt(stored, 10) : 0;
    } catch {
      return 0;
    }
  }, []);

  // Set login time in localStorage (only if not already set)
  const initLoginTime = useCallback(() => {
    try {
      if (!localStorage.getItem(LS_SESSION_LOGIN_TIME)) {
        localStorage.setItem(LS_SESSION_LOGIN_TIME, Date.now().toString());
        // Clear any stale warning start time from previous session
        localStorage.removeItem(LS_WARNING_START_TIME);
        console.log('[QWA-SESSION] Login time initialized, cleared stale warning time');
      }
    } catch (e) {
      console.error('[QWA-SESSION] Error setting login time:', e);
    }
  }, []);

  // Clear login time (called on logout)
  const clearLoginTime = useCallback(() => {
    try {
      localStorage.removeItem(LS_SESSION_LOGIN_TIME);
      localStorage.removeItem(LS_WARNING_START_TIME);
    } catch (e) {
      console.error('[QWA-SESSION] Error clearing login time:', e);
    }
  }, []);

  // Get warning start time from localStorage (shared across tabs for sync)
  const getWarningStartTime = useCallback((): number => {
    try {
      const stored = localStorage.getItem(LS_WARNING_START_TIME);
      return stored ? parseInt(stored, 10) : 0;
    } catch {
      return 0;
    }
  }, []);

  // Set warning start time in localStorage (for cross-tab sync)
  const setWarningStartTime = useCallback(() => {
    try {
      const existingWarningTime = localStorage.getItem(LS_WARNING_START_TIME);
      const loginTime = localStorage.getItem(LS_SESSION_LOGIN_TIME);

      // Clear stale warning time if it's older than login time
      if (existingWarningTime && loginTime && parseInt(existingWarningTime, 10) < parseInt(loginTime, 10)) {
        console.log('[QWA-SESSION] Clearing stale warning time from previous session');
        localStorage.removeItem(LS_WARNING_START_TIME);
      }

      if (!localStorage.getItem(LS_WARNING_START_TIME)) {
        localStorage.setItem(LS_WARNING_START_TIME, Date.now().toString());
        console.log('[QWA-SESSION] Warning start time set');
      }
    } catch (e) {
      console.error('[QWA-SESSION] Error setting warning start time:', e);
    }
  }, []);

  // Re-login handler (for absolute session expiry)
  const reLogin = useCallback(() => {
    console.log('[QWA-SESSION] Redirecting to login for re-authentication');
    clearLoginTime();
    // Preserve current page URL to redirect back after login
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/api/auth/login?returnTo=${returnTo}`;
  }, [clearLoginTime]);

  // Touch the session endpoint to keep it alive
  // Deduplicates concurrent calls — if a request is already in-flight,
  // subsequent callers reuse the same promise to avoid racing on refresh tokens.
  const touchSession = useCallback((): Promise<boolean> => {
    if (touchInFlightRef.current) {
      console.log('[QWA-SESSION] Touch already in-flight, reusing existing request');
      return touchInFlightRef.current;
    }

    const request = (async (): Promise<boolean> => {
      try {
        console.log('[QWA-SESSION] Touching session at', new Date().toLocaleTimeString());

        const response = await fetch('/api/auth/me', {
          method: 'GET',
          credentials: 'include',
        });

        if (!isMountedRef.current) return false;

        if (response.status === 401) {
          console.log('[QWA-SESSION] Session expired (401), logging out');
          clearLoginTime(); // Clear so next login gets fresh timestamp
          await signOut({ callbackUrl: '/', redirect: true });
          return false;
        }

        if (response.ok) {
          console.log('[QWA-SESSION] Session extended successfully');
          return true;
        }

        console.log('[QWA-SESSION] Unexpected response:', response.status);
        return false;
      } catch (error) {
        console.error('[QWA-SESSION] Error touching session:', error);
        return false;
      } finally {
        touchInFlightRef.current = null;
      }
    })();

    touchInFlightRef.current = request;
    return request;
  }, [clearLoginTime]);

  // Close warning and reset state (used when another tab extends session)
  const closeWarning = useCallback(() => {
    console.log('[QWA-SESSION] Closing warning modal');
    setShowWarning(false);
    isWarningShownRef.current = false;
    isAbsoluteWarningRef.current = false;
    setIsAbsoluteExpiry(false);
    setSecondsRemaining(0);

    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Cancel warning handler - user clicked Cancel to dismiss and save work
  // This prevents the warning from appearing again until next session
  const cancelWarningHandler = useCallback(() => {
    console.log('[QWA-SESSION] User dismissed warning to save work');
    isWarningDismissedRef.current = true;
    closeWarning();
  }, [closeWarning]);

  // Logout handler
  const logout = useCallback(async () => {
    console.log('[QWA-SESSION] User requested logout');
    closeWarning();
    clearLoginTime(); // Clear so next login gets fresh timestamp
    await signOut({ callbackUrl: '/', redirect: true });
  }, [closeWarning, clearLoginTime]);

  // Extend session (called when user clicks "Stay Logged In")
  // Note: With silent auto-extend, this is only used as a fallback
  const extendSession = useCallback(async () => {
    console.log('[QWA-SESSION] User clicked Stay Logged In');
    closeWarning();
    await touchSession();
  }, [closeWarning, touchSession]);

  // Initial setup and keep-alive interval
  useEffect(() => {
    console.log('[QWA-SESSION] Hook initialized with config:', {
      SESSION_TIMEOUT_SECONDS,
      KEEP_ALIVE_INTERVAL_SECONDS,
      ABSOLUTE_TIMEOUT_SECONDS,
      ABSOLUTE_WARNING_THRESHOLD_SECONDS,
    });

    isMountedRef.current = true;

    // Initialize login time if not set (new session)
    initLoginTime();

    // Touch session immediately on mount
    touchSession();

    // Start warning countdown for absolute session expiry only
    const startAbsoluteWarningCountdown = () => {
      // Don't show if already shown or if user dismissed it to save work
      if (isWarningShownRef.current || isWarningDismissedRef.current) return;

      // Set warning start time in localStorage (for cross-tab sync)
      // Only sets if not already set by another tab
      setWarningStartTime();

      // Calculate remaining seconds from shared warning start time (synced across tabs)
      const warningStartTime = getWarningStartTime();
      const now = Date.now();
      const elapsedSinceWarning = (now - warningStartTime) / 1000;
      const remainingSeconds = Math.max(0, Math.floor(ABSOLUTE_WARNING_THRESHOLD_SECONDS - elapsedSinceWarning));

      console.log(
        `[SessionKeepAlive] Starting ABSOLUTE warning countdown (${remainingSeconds}s remaining, synced from localStorage)`
      );

      isWarningShownRef.current = true;
      isAbsoluteWarningRef.current = true;
      setIsAbsoluteExpiry(true);
      setSecondsRemaining(remainingSeconds);
      setShowWarning(true);

      countdownRef.current = setInterval(() => {
        // Recalculate from shared warning start time each tick (ensures sync)
        const currentWarningStart = getWarningStartTime();
        const currentNow = Date.now();
        const elapsed = (currentNow - currentWarningStart) / 1000;
        const remaining = Math.max(0, Math.floor(ABSOLUTE_WARNING_THRESHOLD_SECONDS - elapsed));

        setSecondsRemaining(remaining);

        if (remaining <= 0) {
          console.log('[QWA-SESSION] Countdown finished');
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
        }
      }, 1000);
    };

    // Check if we should show warning
    const checkSessionStatus = () => {
      const now = Date.now();

      // Check absolute session first (takes priority)
      const loginTime = getLoginTime();
      if (loginTime > 0) {
        const timeSinceLogin = (now - loginTime) / 1000;
        const timeUntilAbsoluteExpiry = ABSOLUTE_TIMEOUT_SECONDS - timeSinceLogin;

        console.log('[QWA-SESSION] Absolute session check:', {
          timeSinceLogin: Math.floor(timeSinceLogin) + 's',
          timeUntilAbsoluteExpiry: Math.floor(timeUntilAbsoluteExpiry) + 's',
          absoluteWarningAt: ABSOLUTE_WARNING_THRESHOLD_SECONDS + 's',
        });

        // Show absolute expiry warning (cannot be extended, must re-login)
        // Skip if user already dismissed to save work
        if (
          timeUntilAbsoluteExpiry <= ABSOLUTE_WARNING_THRESHOLD_SECONDS &&
          !isWarningShownRef.current &&
          !isWarningDismissedRef.current
        ) {
          startAbsoluteWarningCountdown();
          return;
        }

        // Already expired - show warning if not already shown, but don't auto-logout
        // User must click "Logout" or "Re-login" manually
        // Skip if user already dismissed to save work
        if (timeUntilAbsoluteExpiry <= 0 && !isWarningShownRef.current && !isWarningDismissedRef.current) {
          console.log('[QWA-SESSION] Absolute session expired, showing warning');
          startAbsoluteWarningCountdown();
          return;
        }
      }

      // Rolling/idle session is handled silently - no warning popup
      // The touchSession() call in the periodic interval keeps the session alive automatically
      // as long as the app is open
    };

    // Set up periodic keep-alive and status check
    // Always touch session to silently extend rolling session (no idle warning)
    keepAliveRef.current = setInterval(() => {
      touchSession();
      checkSessionStatus();
    }, KEEP_ALIVE_INTERVAL_SECONDS * 1000);

    // Visibility change - touch session when tab becomes visible
    const handleVisibility = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[QWA-SESSION] Tab became visible');
        await touchSession();
        checkSessionStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Cross-tab sync: Listen for storage events from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      // Another tab started showing warning (warning start time was set)
      // Show warning in this tab too (synced countdown)
      if (event.key === LS_WARNING_START_TIME && !event.oldValue && event.newValue) {
        console.log('[QWA-SESSION] Another tab started warning, syncing this tab');
        if (!isWarningShownRef.current && !isWarningDismissedRef.current) {
          startAbsoluteWarningCountdown();
        }
      }

      // Another tab clicked "Re-login Now" (login time was cleared)
      // Just close the warning and wait - don't reload yet
      if (event.key === LS_SESSION_LOGIN_TIME && event.oldValue && !event.newValue) {
        console.log('[QWA-SESSION] Another tab initiated re-login, waiting for completion');
        closeWarning();
        // Don't reload yet - wait for the other tab to complete login
      }

      // Another tab completed re-login (new login time was set)
      // Now it's safe to reload to pick up the new session
      if (event.key === LS_SESSION_LOGIN_TIME && !event.oldValue && event.newValue) {
        console.log('[QWA-SESSION] Another tab completed re-login, reloading this tab');
        window.location.reload();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Cleanup
    return () => {
      console.log('[QWA-SESSION] Cleanup');
      isMountedRef.current = false;

      if (keepAliveRef.current) {
        clearInterval(keepAliveRef.current);
        keepAliveRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }

      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [
    touchSession,
    closeWarning,
    initLoginTime,
    getLoginTime,
    clearLoginTime,
    getWarningStartTime,
    setWarningStartTime,
  ]);

  return {
    showWarning,
    secondsRemaining,
    isAbsoluteExpiry,
    extendSession,
    logout,
    reLogin,
    cancelWarning: cancelWarningHandler,
  };
};

export default useSessionKeepAlive;
